import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FinancialMetricCard from './components/FinancialMetricCard';
import RevenueAnalyticsChart from './components/RevenueAnalyticsChart';
import CashFlowChart from './components/CashFlowChart';
import ProfitMarginChart from './components/ProfitMarginChart';
import BudgetVarianceWidget from './components/BudgetVarianceWidget';
import FinancialAlertsPanel from './components/FinancialAlertsPanel';
import FinancialFilters from './components/FinancialFilters';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const FinancialAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    dateRange: '30d',
    currency: 'USD',
    department: 'all'
  });

  // Mock data for financial metrics
  const financialMetrics = [
    {
      id: 'total-revenue',
      title: 'Total Revenue',
      value: '$3.2M',
      change: '+15.3%',
      changeType: 'positive',
      icon: 'DollarSign',
      trend: [52, 58, 45, 67, 73, 89, 95, 78, 85, 92, 88, 98],
      period: 'This Quarter'
    },
    {
      id: 'profit-margin',
      title: 'Profit Margin',
      value: '24.8%',
      change: '+3.2%',
      changeType: 'positive',
      icon: 'TrendingUp',
      trend: [45, 52, 48, 65, 71, 83, 89, 76, 82, 87, 84, 91],
      period: 'This Month'
    },
    {
      id: 'cash-flow',
      title: 'Cash Flow',
      value: '$847K',
      change: '-2.1%',
      changeType: 'negative',
      icon: 'CreditCard',
      trend: [78, 65, 89, 76, 45, 52, 48, 67, 73, 82, 79, 75],
      period: 'This Week'
    },
    {
      id: 'budget-variance',
      title: 'Budget Variance',
      value: '+$125K',
      change: '+8.7%',
      changeType: 'positive',
      icon: 'Target',
      trend: [67, 73, 89, 95, 78, 85, 92, 88, 94, 89, 91, 96],
      period: 'YTD'
    },
    {
      id: 'operational-costs',
      title: 'Operational Costs',
      value: '$1.8M',
      change: '-4.5%',
      changeType: 'positive',
      icon: 'Calculator',
      trend: [89, 85, 78, 72, 68, 65, 62, 58, 55, 52, 48, 45],
      period: 'This Quarter'
    },
    {
      id: 'roi',
      title: 'Return on Investment',
      value: '18.4%',
      change: '+6.2%',
      changeType: 'positive',
      icon: 'PieChart',
      trend: [32, 45, 58, 64, 78, 82, 89, 93, 87, 91, 94, 97],
      period: 'Annual'
    }
  ];

  // Mock data for revenue analytics
  const revenueData = [
    { month: 'Jan', revenue: 265000, forecast: 250000, actual: 268000 },
    { month: 'Feb', revenue: 278000, forecast: 270000, actual: 285000 },
    { month: 'Mar', revenue: 245000, forecast: 260000, actual: 242000 },
    { month: 'Apr', revenue: 315000, forecast: 295000, actual: 318000 },
    { month: 'May', revenue: 342000, forecast: 320000, actual: 345000 },
    { month: 'Jun', revenue: 368000, forecast: 350000, actual: 372000 },
    { month: 'Jul', revenue: 391000, forecast: 375000, actual: 394000 },
    { month: 'Aug', revenue: 356000, forecast: 370000, actual: 359000 },
    { month: 'Sep', revenue: 387000, forecast: 380000, actual: 392000 },
    { month: 'Oct', revenue: 425000, forecast: 400000, actual: 428000 },
    { month: 'Nov', revenue: 398000, forecast: 410000, actual: 402000 },
    { month: 'Dec', revenue: 456000, forecast: 440000, actual: 459000 }
  ];

  // Mock data for cash flow
  const cashFlowData = [
    { month: 'Jan', inflow: 285000, outflow: 215000, net: 70000 },
    { month: 'Feb', inflow: 298000, outflow: 225000, net: 73000 },
    { month: 'Mar', inflow: 265000, outflow: 240000, net: 25000 },
    { month: 'Apr', inflow: 335000, outflow: 255000, net: 80000 },
    { month: 'May', inflow: 362000, outflow: 275000, net: 87000 },
    { month: 'Jun', inflow: 388000, outflow: 295000, net: 93000 },
    { month: 'Jul', inflow: 411000, outflow: 315000, net: 96000 },
    { month: 'Aug', inflow: 376000, outflow: 305000, net: 71000 },
    { month: 'Sep', inflow: 407000, outflow: 325000, net: 82000 },
    { month: 'Oct', inflow: 445000, outflow: 345000, net: 100000 },
    { month: 'Nov', inflow: 418000, outflow: 335000, net: 83000 },
    { month: 'Dec', inflow: 476000, outflow: 355000, net: 121000 }
  ];

  // Mock data for profit margins
  const profitMarginData = [
    { quarter: 'Q1 2024', grossMargin: 45.2, operatingMargin: 28.7, netMargin: 18.4 },
    { quarter: 'Q2 2024', grossMargin: 47.8, operatingMargin: 31.2, netMargin: 21.6 },
    { quarter: 'Q3 2024', grossMargin: 44.3, operatingMargin: 27.9, netMargin: 17.8 },
    { quarter: 'Q4 2024', grossMargin: 49.1, operatingMargin: 33.4, netMargin: 24.2 },
    { quarter: 'Q1 2025', grossMargin: 51.2, operatingMargin: 35.8, netMargin: 26.1 },
    { quarter: 'Q2 2025', grossMargin: 48.7, operatingMargin: 32.3, netMargin: 23.9 },
    { quarter: 'Q3 2025', grossMargin: 52.4, operatingMargin: 37.1, netMargin: 28.5 }
  ];

  // Mock data for budget variance
  const budgetVarianceData = [
    { category: 'Revenue', budget: 3500000, actual: 3685000, variance: 185000, percentage: 5.3 },
    { category: 'COGS', budget: 1750000, actual: 1692000, variance: -58000, percentage: -3.3 },
    { category: 'Marketing', budget: 420000, actual: 445000, variance: 25000, percentage: 6.0 },
    { category: 'Operations', budget: 680000, actual: 652000, variance: -28000, percentage: -4.1 },
    { category: 'R&D', budget: 320000, actual: 335000, variance: 15000, percentage: 4.7 },
    { category: 'Admin', budget: 280000, actual: 267000, variance: -13000, percentage: -4.6 }
  ];

  // Mock data for financial alerts
  const financialAlerts = [
    {
      id: 1,
      type: 'critical',
      title: 'Budget Overrun Alert',
      message: 'Marketing department has exceeded budget by 15% this quarter',
      amount: '$63,750',
      timestamp: new Date(Date.now() - 25 * 60 * 1000),
      category: 'budget'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Cash Flow Projection',
      message: 'Projected cash flow shortfall in Q4 2025 requires attention',
      amount: '-$125,000',
      timestamp: new Date(Date.now() - 45 * 60 * 1000),
      category: 'cashflow'
    },
    {
      id: 3,
      type: 'info',
      title: 'Revenue Milestone',
      message: 'Quarterly revenue target exceeded by 8.3%',
      amount: '+$276,500',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      category: 'revenue'
    },
    {
      id: 4,
      type: 'warning',
      title: 'Expense Approval Required',
      message: 'Capital expenditure request pending finance approval',
      amount: '$450,000',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
      category: 'approval'
    },
    {
      id: 5,
      type: 'critical',
      title: 'Payment Due Alert',
      message: 'Vendor payments totaling $125K due within 48 hours',
      amount: '$125,000',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      category: 'payment'
    }
  ];

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMetricClick = (metricId) => {
    console.log(`Financial metric clicked: ${metricId}`);
    // Navigate to detailed financial analysis
  };

  const handleFiltersChange = (filters) => {
    setSelectedFilters(filters);
    console.log('Financial filters changed:', filters);
    // Update dashboard data based on selected filters
  };

  const handleExportFinancialData = () => {
    console.log('Exporting financial analytics data...');
    // Implement financial data export functionality
  };

  const handleDrillDownAnalysis = () => {
    console.log('Opening drill-down analysis...');
    // Open detailed transaction analysis modal
  };

  const handleGoToReports = () => {
    navigate('/financial-reports-center');
  };

  useEffect(() => {
    // Simulate real-time financial data updates
    const interval = setInterval(() => {
      console.log('Updating financial data...');
      // Update financial metrics and charts with real-time data
    }, 60000); // Update every minute for financial data

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main 
        className={`transition-smooth pt-16 ${
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        }`}
      >
        <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <Breadcrumb />
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
                  <Icon name="PieChart" size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Financial Analytics Dashboard</h1>
                  <p className="text-muted-foreground">Comprehensive financial insights and performance monitoring</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <FinancialFilters onFiltersChange={handleFiltersChange} />
              <Button variant="outline" iconName="BarChart3" onClick={handleDrillDownAnalysis}>
                Drill Down
              </Button>
              <Button variant="outline" iconName="Download" onClick={handleExportFinancialData}>
                Export
              </Button>
              <Button variant="default" iconName="FileText" onClick={handleGoToReports}>
                Reports
              </Button>
            </div>
          </div>

          {/* Financial KPIs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {financialMetrics?.map((metric) => (
              <FinancialMetricCard
                key={metric?.id}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                trend={metric?.trend}
                period={metric?.period}
                onClick={() => handleMetricClick(metric?.id)}
              />
            ))}
          </div>

          {/* Main Analytics Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div>
              <RevenueAnalyticsChart data={revenueData} />
            </div>
            <div>
              <CashFlowChart data={cashFlowData} />
            </div>
          </div>

          {/* Secondary Analytics */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div>
              <ProfitMarginChart data={profitMarginData} />
            </div>
            <div>
              <BudgetVarianceWidget data={budgetVarianceData} />
            </div>
          </div>

          {/* Financial Alerts Panel */}
          <div>
            <FinancialAlertsPanel alerts={financialAlerts} />
          </div>

          {/* Real-time Financial Status */}
          <div className="fixed bottom-6 right-6 z-50">
            <div className="bg-card border border-border rounded-full px-4 py-2 card-shadow flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span className="text-xs text-muted-foreground">Financial Data Live</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FinancialAnalyticsDashboard;